/**
 * MCO auth gate — every DC session must hold a Sngine bearer from
 * MegaCity.Online. The bearer is obtained by opening the NEXUS Vault's
 * sign-in flow as a popup; the vault posts the token back via
 * `postMessage` after the user authorizes on megacity.online.
 *
 * This is separate from `nrBase`'s `dc:nr-token` (which authenticates DC
 * to its NR runtime). Eventually those merge — for now they run in
 * parallel: DC users must hold BOTH tokens.
 */

const VAULT_BASE =
  (import.meta as unknown as { env: Record<string, string | undefined> }).env
    .VITE_VAULT_BASE ?? 'https://vault.psyopsys.com'

const TOKEN_KEY = 'mco_access_token'
const EXPIRES_KEY = 'mco_access_token_expires'

export interface McoIdentity {
  subject: string
  idp: string
  displayName?: string
  email?: string
  verified: boolean
}

export type McoAuthStatus = 'unauth' | 'pending' | 'authed' | 'expired'

export interface McoTenant {
  id: number
  slug: string
  name: string
  agentName?: string
  /** a tenant VIEWED by grant (ticket #9): its grant's access */
  access?: string
}

interface McoAuthState {
  status: McoAuthStatus
  token: string | null
  expiresAt: Date | null
  identity: McoIdentity | null
  /** The user's primary tenant, resolved via vault /vault/tenant after sign-in.
   *  Used to scope mu-api calls via X-Tenant-Id header. Null until resolved
   *  (treated as "tenant 1 / operator default" by mu-api's fallback). */
  tenant: McoTenant | null
  /** Ticket #9 (2026-10-05) — the MU agent-memory switcher: the tenants this person may VIEW by grant (a sponsor's
   *  view of its agents' memory — mu-api GET /tenants/viewable), and the one being viewed now (null = their own).
   *  While viewing, every mu-api call carries its id; mu-api serves reads only and bills the viewer. */
  viewable: McoTenant[]
  viewing: McoTenant | null
  /** the person's OWN memory as mu-api resolves it (its primary tenant) — the Vault's /vault/tenant can name another
   *  (a first membership: a moderator seat), so the switcher and the scope header use this one */
  own: McoTenant | null
  error: string | null
}

export const mco = $state<McoAuthState>({
  status: 'unauth',
  token: null,
  expiresAt: null,
  identity: null,
  tenant: null,
  viewable: [],
  viewing: null,
  own: null,
  error: null,
})

/** Boot — call once at app start. Reads cached token; verifies with vault. */
export function startMcoAuth(): void {
  // Always register the bridge listener (popup can fire even on retry).
  window.addEventListener('message', onBridgeMessage)

  const cached = localStorage.getItem(TOKEN_KEY)
  const cachedExpires = localStorage.getItem(EXPIRES_KEY)
  if (cached) {
    mco.token = cached
    mco.expiresAt = cachedExpires ? new Date(cachedExpires) : null
    mco.status = 'pending'
    verifyAndHydrate(cached).catch(err => {
      mco.error = String(err)
      mco.status = 'unauth'
    })
  }
}

/** Trigger sign-in popup. Resolves when the bridge delivers a token. */
export function signIn(): Promise<void> {
  const url = `${VAULT_BASE}/oauth/sngine/login`
  const popup = window.open(url, 'nexus-vault-auth', 'width=520,height=720,popup')
  if (!popup) {
    mco.error = 'popup blocked — allow popups for this site'
    return Promise.reject(new Error('popup blocked'))
  }
  mco.status = 'pending'
  mco.error = null
  return new Promise(resolve => {
    const stopAt = Date.now() + 5 * 60 * 1000 // 5min max
    const tick = setInterval(() => {
      if (mco.status === 'authed') {
        clearInterval(tick)
        resolve()
      } else if (Date.now() > stopAt) {
        clearInterval(tick)
        mco.status = 'unauth'
        mco.error = 'sign-in timed out'
        resolve()
      }
    }, 250)
  })
}

export function signOut(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(EXPIRES_KEY)
  mco.token = null
  mco.expiresAt = null
  mco.identity = null
  mco.tenant = null
  mco.viewable = []
  mco.viewing = null
  mco.own = null
  mco.status = 'unauth'
  mco.error = null
}

/**
 * Mid-session expiry. A mu-api call just rejected our bearer with 401 — the
 * token died while the tab was open. The boot-time `verifyAndHydrate` only
 * runs once at startup, so without this the app keeps polling with a dead
 * token behind a still-'authed' status and silently shows stale data (the
 * task-graph freeze: a 401 on /api/tasks/version was read as "nothing
 * changed"). Mirror verifyAndHydrate's 401 path — drop the dead token and flip
 * to 'expired' so McoAuthGate re-prompts sign-in. Idempotent; no-op when we
 * never held a bearer (the gate already covers the plain 'unauth' case).
 */
export function noteBearerRejected(): void {
  if (mco.token == null) return
  signOut()
  mco.status = 'expired'
}

/**
 * Iframe-host bearer hand-off. Called by addonProtocol when DC posts an
 * `'auth'` message containing the user's MCO bearer. We adopt the bearer
 * IN-MEMORY only — we deliberately DON'T persist it to mu.psyopsys.ai's
 * localStorage. The iframe instance has the same trust as the host, but
 * if the user later opens mu.psyopsys.ai standalone we don't want a stale
 * host-cached token to leak across sessions.
 *
 * The host re-posts whenever its bearer changes, so iframe state stays
 * in lockstep without needing local persistence.
 */
export function acceptHostBearer(payload: {
  token: string
  expiresAt?: string | null
  identity?: McoIdentity | null
}): void {
  if (!payload || typeof payload.token !== 'string' || payload.token.length === 0) return
  mco.token = payload.token
  mco.expiresAt = payload.expiresAt ? new Date(payload.expiresAt) : null
  mco.identity = payload.identity ?? null
  mco.status = 'authed'
  mco.error = null
  // Resolve tenant so api.svelte sends X-Tenant-Id from the first call
  fetchTenant(payload.token).catch((err) => {
    console.warn('[mcoAuth] host-bearer tenant fetch failed:', String(err))
  })
}

// ── Internals ─────────────────────────────────────────────────────────────

function onBridgeMessage(ev: MessageEvent): void {
  if (ev.origin !== VAULT_BASE) return
  const data = ev.data
  if (!data || data.type !== 'mco-auth' || typeof data.token !== 'string') return

  mco.token = data.token
  mco.expiresAt = data.expiresAt ? new Date(data.expiresAt) : null
  mco.identity = data.identity ?? null
  mco.status = 'authed'
  mco.error = null

  localStorage.setItem(TOKEN_KEY, data.token)
  if (data.expiresAt) localStorage.setItem(EXPIRES_KEY, data.expiresAt)

  // Resolve tenant for the just-signed-in user; non-blocking
  fetchTenant(data.token).catch((err) => {
    // Soft failure: identity is good even if tenant call hiccups. mu-api
    // will fall through to its default-tenant-1 behavior until next refresh.
    console.warn('[mcoAuth] tenant fetch failed:', String(err))
  })
}

async function verifyAndHydrate(token: string): Promise<void> {
  const resp = await fetch(`${VAULT_BASE}/oauth/sngine/me`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (resp.status === 401) {
    signOut()
    mco.status = 'expired'
    return
  }
  if (!resp.ok) {
    mco.status = 'unauth'
    mco.error = `vault verify ${resp.status}`
    return
  }
  const json = await resp.json()
  mco.identity = json.identity ?? null
  mco.status = 'authed'

  // Resolve tenant in parallel — non-blocking on the auth path
  fetchTenant(token).catch((err) => {
    console.warn('[mcoAuth] tenant fetch failed:', String(err))
  })
}

/**
 * Resolve the user's primary tenant via vault /vault/tenant.
 * The response shape is { tenant: {id, slug, name, agentName?}, role }.
 * Cached in mco.tenant for api.svelte.ts to read.
 */
async function fetchTenant(token: string): Promise<void> {
  const resp = await fetch(`${VAULT_BASE}/vault/tenant`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!resp.ok) {
    mco.tenant = null
    return
  }
  const json = await resp.json()
  const t = json?.tenant
  mco.tenant = t
    ? {
        id: Number(t.id),
        slug: String(t.slug),
        name: String(t.name),
        agentName: t.agentName,
      }
    : null
}
